import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../api/auth';
import { PageHeader, StatusBadge, ConfirmDialog } from '../../components/ui';

const SettingsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileErrors, setProfileErrors] = useState<{ name?: string; email?: string }>({});
  const [passwordErrors, setPasswordErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name);
    setEmail(user.email);
  }, [user]);

  const validateProfile = () => {
    const next: typeof profileErrors = {};
    if (!name.trim()) next.name = 'Name is required';
    if (!email.trim()) next.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) next.email = 'Enter a valid email';
    setProfileErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateProfile()) return;
    // No admin profile update endpoint on backend yet
    toast.info('Profile update API is not available yet');
  };

  const validatePassword = () => {
    const next: typeof passwordErrors = {};
    if (!currentPassword) next.currentPassword = 'Current password is required';
    if (!newPassword) next.newPassword = 'New password is required';
    else if (newPassword.length < 8) next.newPassword = 'Password must be at least 8 characters';
    if (!confirmPassword) next.confirmPassword = 'Confirm your new password';
    else if (newPassword !== confirmPassword) next.confirmPassword = 'Passwords do not match';
    setPasswordErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword()) return;
    setPasswordLoading(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordErrors({});
      toast.success('Password updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await logout();
      setLogoutOpen(false);
      navigate('/login');
    } finally {
      setLogoutLoading(false);
    }
  };

  if (!user) {
    return (
      <Box>
        <PageHeader title="Settings" />
        <Typography color="text.secondary">No admin session found.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Settings"
        subtitle="Manage your admin account"
        actions={
          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutOutlinedIcon />}
            onClick={() => setLogoutOpen(true)}
          >
            Log out
          </Button>
        }
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Profile
              </Typography>
              <Box component="form" onSubmit={handleSaveProfile}>
                <Stack spacing={2}>
                  <TextField
                    label="Name"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={Boolean(profileErrors.name)}
                    helperText={profileErrors.name}
                  />
                  <TextField
                    label="Email"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={Boolean(profileErrors.email)}
                    helperText={profileErrors.email}
                  />
                  <Box>
                    <Button type="submit" variant="contained" startIcon={<SaveOutlinedIcon />}>
                      Save Profile
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Change Password
              </Typography>
              <Box component="form" onSubmit={handleChangePassword}>
                <Stack spacing={2}>
                  <TextField
                    label="Current Password"
                    type="password"
                    fullWidth
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    error={Boolean(passwordErrors.currentPassword)}
                    helperText={passwordErrors.currentPassword}
                  />
                  <TextField
                    label="New Password"
                    type="password"
                    fullWidth
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    error={Boolean(passwordErrors.newPassword)}
                    helperText={passwordErrors.newPassword}
                  />
                  <TextField
                    label="Confirm New Password"
                    type="password"
                    fullWidth
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={Boolean(passwordErrors.confirmPassword)}
                    helperText={passwordErrors.confirmPassword}
                  />
                  <Box>
                    <Button type="submit" variant="contained" disabled={passwordLoading}>
                      {passwordLoading ? 'Updating…' : 'Update Password'}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Account
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Admin ID
              </Typography>
              <Typography fontWeight={600} sx={{ mb: 1.5 }}>
                {user.id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Role
              </Typography>
              <Typography fontWeight={600} sx={{ mb: 1.5 }}>
                {user.role}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Status
              </Typography>
              <StatusBadge status={user.status} />
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Signed in as {user.email}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={logoutOpen}
        title="Log out"
        message="Are you sure you want to log out of HomeHero Admin?"
        confirmLabel="Log out"
        danger
        loading={logoutLoading}
        onConfirm={handleLogout}
        onClose={() => !logoutLoading && setLogoutOpen(false)}
      />
    </Box>
  );
};

export default SettingsPage;
